import { Check, CreditCard, FileText, Gavel, Microscope, X as XIcon } from "lucide-react";

/**
 * Stepper visual del flujo simple de solicitudes (Telecom y Seguridad). Mapea
 * los estados a 5 pasos: 4 etapas operativas + estado final.
 *
 * Mapping:
 *  - Activos:     SOLICITUD → FACTURACION → EVALUACION → REVISION
 *  - Terminales:  ACEPTADA (éxito en el paso final) | RECHAZO_<ETAPA> (rojo en esa etapa)
 *
 * «Revisión y decisión» es una sola etapa desde V172: absorbió la antigua
 * «Toma de decisión». Los estados jubilados TOMA_DECISION y
 * RECHAZO_TOMA_DECISION siguen apareciendo en el mapa de rechazos porque hay
 * solicitudes históricas que pasaron por ellos y su detalle tiene que seguir
 * pintándose.
 *
 * El componente es agnóstico del histórico; solo necesita el estado actual y
 * si la instancia está cerrada (es_final).
 */
interface Etapa {
  codigo: string;
  nombre: string;
  Icono: React.ComponentType<{ className?: string }>;
}

const ETAPAS: Etapa[] = [
  { codigo: "SOLICITUD",     nombre: "Solicitud",        Icono: FileText },
  { codigo: "FACTURACION",   nombre: "Facturación",      Icono: CreditCard },
  { codigo: "EVALUACION",    nombre: "Evaluación",       Icono: Microscope },
  { codigo: "REVISION",      nombre: "Revisión y decisión", Icono: Gavel },
];

/**
 * Nombre de la etapa final por defecto. Los servicios cuyo resultado no es un
 * certificado lo sobreescriben vía la prop {@code etapaFinalNombre} (viene de
 * kernel.servicio.documento_resultado — misma configuración que «Aceptar y
 * emitir…» y «Ver…»); así los servicios nuevos se ajustan sin tocar código.
 */
const ETAPA_FINAL_DEFAULT = "Certificado";

/** Mapea cada estado RECHAZO_X al código de etapa en la que ocurrió. */
const RECHAZO_EN_ETAPA: Record<string, string> = {
  RECHAZO_SOLICITUD: "SOLICITUD",
  RECHAZO_FACTURACION: "FACTURACION",
  RECHAZO_EVALUACION: "EVALUACION",
  RECHAZO_REVISION: "REVISION",
  // Jubilado en V172, pero el histórico lo conserva: se pinta en la etapa que
  // lo absorbió para que una solicitud vieja no quede sin paso donde marcarse.
  RECHAZO_TOMA_DECISION: "REVISION",
};

/**
 * Estados jubilados que se pintan sobre la etapa que los absorbió. Sin esto,
 * una solicitud histórica detenida en TOMA_DECISION no encontraría su paso.
 */
const ETAPA_ABSORBIDA: Record<string, string> = {
  TOMA_DECISION: "REVISION",
  // OBSERVACIONES no es un paso más: es la etapa de revisión esperando al
  // cliente. Se dibuja sobre ella para no alargar el recorrido con un paso que
  // la mayoría de solicitudes nunca pisa.
  OBSERVACIONES: "REVISION",
};

type Estado = "done" | "current" | "pending" | "rejected";

export function FlujoStepper({
  estadoCodigo,
  cerrada,
  etapaFinalNombre,
}: {
  estadoCodigo: string | null;
  cerrada: boolean;
  /** Nombre de la etapa final según el servicio (p. ej. «Carta aduana»). */
  etapaFinalNombre?: string | null;
}) {
  if (!estadoCodigo) return null;

  const nombreFinal = etapaFinalNombre?.trim()
    ? etapaFinalNombre.trim().charAt(0).toUpperCase() + etapaFinalNombre.trim().slice(1)
    : ETAPA_FINAL_DEFAULT;

  const etapaRechazada = RECHAZO_EN_ETAPA[estadoCodigo] ?? null;
  const esAceptada = estadoCodigo === "ACEPTADA";
  // Un estado jubilado se dibuja en la etapa que lo absorbió.
  const codigoVigente = ETAPA_ABSORBIDA[estadoCodigo] ?? estadoCodigo;

  const calcular = (etapaCodigo: string): Estado => {
    if (etapaRechazada) {
      const idxRech = ETAPAS.findIndex((e) => e.codigo === etapaRechazada);
      const idxEtapa = ETAPAS.findIndex((e) => e.codigo === etapaCodigo);
      if (idxEtapa < idxRech) return "done";
      if (idxEtapa === idxRech) return "rejected";
      return "pending";
    }
    if (esAceptada) return "done";
    const idxActual = ETAPAS.findIndex((e) => e.codigo === codigoVigente);
    const idxEtapa = ETAPAS.findIndex((e) => e.codigo === etapaCodigo);
    if (idxEtapa < idxActual) return "done";
    if (idxEtapa === idxActual) return "current";
    return "pending";
  };

  const finalEstado: Estado = etapaRechazada
    ? "pending"
    : esAceptada
    ? "done"
    : cerrada
    ? "pending"
    : "pending";

  const finalEtapa: Etapa = etapaRechazada
    ? {
        codigo: estadoCodigo,
        nombre: "Rechazada",
        Icono: XIcon,
      }
    : { codigo: "ACEPTADA", nombre: nombreFinal, Icono: Check };

  const finalEstadoFinal: Estado = etapaRechazada ? "rejected" : finalEstado;

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-sm">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        Etapa de la solicitud
      </h2>
      <ol className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-0">
        {ETAPAS.map((etapa, i) => (
          <Paso
            key={etapa.codigo}
            etapa={etapa}
            estado={calcular(etapa.codigo)}
            esUltimo={false}
            siguienteEstado={i < ETAPAS.length - 1 ? calcular(ETAPAS[i + 1].codigo) : finalEstadoFinal}
          />
        ))}
        <Paso etapa={finalEtapa} estado={finalEstadoFinal} esUltimo={true} siguienteEstado={finalEstadoFinal} />
      </ol>
    </section>
  );
}

function Paso({
  etapa,
  estado,
  esUltimo,
  siguienteEstado,
}: {
  etapa: Etapa;
  estado: Estado;
  esUltimo: boolean;
  siguienteEstado: Estado;
}) {
  const colors = colorsPorEstado(estado);
  const Icono = etapa.Icono;
  return (
    <li className="flex flex-1 items-start gap-3 sm:flex-col sm:items-center sm:gap-1">
      <div className="relative flex w-full items-center sm:flex-col">
        <div
          className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${colors.circle}`}
        >
          {estado === "done" ? (
            <Check className="h-4 w-4" />
          ) : estado === "rejected" ? (
            <XIcon className="h-4 w-4" />
          ) : (
            <Icono className="h-4 w-4" />
          )}
        </div>
        {!esUltimo && (
          <div className="hidden sm:block sm:absolute sm:left-1/2 sm:top-[18px] sm:h-0.5 sm:w-full">
            <div className={`h-0.5 w-full ${conector(siguienteEstado)}`} />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-0.5 sm:items-center sm:text-center">
        <span className={`text-xs font-medium ${colors.label}`}>{etapa.nombre}</span>
        <span className={`text-[10px] uppercase tracking-wider ${colors.subtitle}`}>
          {textoEstado(estado)}
        </span>
      </div>
    </li>
  );
}

function colorsPorEstado(estado: Estado) {
  switch (estado) {
    case "done":
      return {
        circle: "border-action-ok bg-action-ok text-white",
        label: "text-estado-ok",
        subtitle: "text-estado-ok",
      };
    case "current":
      return {
        circle: "border-primary-strong bg-surface text-primary-strong ring-4 ring-primary/20",
        label: "text-primary-strong",
        subtitle: "text-primary-strong",
      };
    case "rejected":
      return {
        circle: "border-action-err bg-action-err text-white",
        label: "text-estado-err",
        subtitle: "text-estado-err",
      };
    case "pending":
    default:
      return {
        circle: "border-edge-strong bg-surface text-fg-faint",
        label: "text-fg-subtle",
        subtitle: "text-fg-faint",
      };
  }
}

function conector(estado: Estado): string {
  if (estado === "done") return "bg-action-ok";
  if (estado === "rejected") return "bg-action-err/50";
  if (estado === "current") return "bg-gradient-to-r from-action-ok to-edge";
  return "bg-edge";
}

function textoEstado(estado: Estado): string {
  switch (estado) {
    case "done":
      return "Completada";
    case "current":
      return "En curso";
    case "rejected":
      return "Rechazada";
    case "pending":
    default:
      return "Pendiente";
  }
}
